/**
 * Account Management Event Tracking
 * Tracks account-related actions (profile, addresses, payment methods, wallet) with error context
 */

import { errorTracker, ErrorSeverity } from './error-tracking';
import { isAnalyticsDisabled } from './analytics-enabled';
import { getSessionId } from './tracking-context';

/**
 * The visit this account action happened in.
 *
 * Returns the ONE browser session from `tracking-context`. It used to mint its
 * own into `sessionStorage['account_session_id']`, with a private
 * `generateUUID()` that returned `${Date.now()}-${base36}` -- the exact shape of
 * a real analytics session. That is what made it invisible: the value passed
 * every shape check, landed in the `session_id` COLUMN of `funnel_events`
 * (`pages/api/analytics/account/track.ts`), and joined to nothing at all.
 *
 * Measured on production 2026-09-18: 0 of 20 account funnel sessions appeared in
 * `events`, while their `anonymous_id` matched 15 of 15 -- the signature of a
 * correct browser id beside a fabricated session id.
 *
 * This is the same defect the 2026-09-16 unification removed from
 * `utils/analytics.ts` and `utils/error-tracking.ts`. It survived in this file
 * and in `utils/track-transaction.ts` because neither was in that conversation.
 * Do not reintroduce a local generator here; see `utils/tracking-context.ts`.
 */
export function getAccountSessionId(): string {
  return getSessionId();
}

export type AccountEventType =
  | 'profile_viewed'
  | 'profile_updated'
  | 'email_changed'
  | 'password_changed'
  | 'address_added'
  | 'address_updated'
  | 'address_deleted'
  | 'payment_method_added'
  | 'payment_method_removed'
  | 'wallet_viewed'
  | 'wallet_deposit'
  | 'wallet_withdrawal'
  | 'settings_updated'
  | 'abandoned';

export type AccountSection =
  | 'profile'
  | 'addresses'
  | 'payment_methods'
  | 'wallet'
  | 'settings';

export interface AccountEventData {
  event_type: AccountEventType;
  user_id?: number;
  section: AccountSection;

  // Profile tracking
  profile_fields_changed?: string[]; // ['name', 'email', 'phone']

  // Address tracking
  address_id?: number;
  is_default_address?: boolean;
  address_type?: 'shipping' | 'billing' | 'both';

  // Payment method tracking
  payment_method_id?: string;
  payment_method_type?: 'card' | 'bank_account' | 'wallet';
  is_default_payment?: boolean;

  // Wallet tracking
  wallet_balance?: number;
  transaction_amount?: number;
  wallet_transaction_id?: string;

  // Settings tracking
  settings_changed?: string[]; // ['notifications', 'privacy', 'security']

  // Error context (optional)
  error?: {
    error_type: 'api_error' | 'network_error' | 'validation_error' | 'stripe_error' | 'notification_error';
    error_message: string;
    error_severity?: 'info' | 'warning' | 'error' | 'critical';
    error_fingerprint?: string;
  };
}

export async function trackAccountEvent(data: AccountEventData) {
  if (isAnalyticsDisabled()) return;

  try {
    const account_session_id = getAccountSessionId();

    const eventPayload = {
      ...data,
      account_session_id,
      timestamp: new Date().toISOString(),

      // Error fields (defaults to 0/NULL if no error)
      error_occurred: data.error ? 1 : 0,
      error_type: data.error?.error_type || null,
      error_message: data.error?.error_message?.substring(0, 500) || null,
      error_severity: data.error?.error_severity || '',
      error_fingerprint: data.error?.error_fingerprint || null,
    };

    // Send to tracking API
    await fetch('/api/analytics/account/track', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(eventPayload),
    });

    // Also log to error_logs table if error occurred (for detailed analysis)
    if (data.error) {
      errorTracker.trackError(
        data.error.error_message,
        (data.error.error_severity as ErrorSeverity) || ErrorSeverity.ERROR,
        {
          user_id: data.user_id,
          session_id: account_session_id,
          funnel_type: 'account',
          funnel_step: data.event_type,
          extra: { section: data.section },
        }
      );
    }
  } catch (error) {
    errorTracker.trackError(
      error instanceof Error ? error : new Error(`[Account Tracking] Failed to track event: ${error}`),
      ErrorSeverity.WARNING
    );
  }
}

/**
 * Helper for tracking account errors
 */
export async function trackAccountError(
  errorType: AccountEventData['error']['error_type'],
  errorMessage: string,
  section: AccountSection,
  eventType: AccountEventType = 'abandoned',
  additionalData?: Partial<AccountEventData>
) {
  await trackAccountEvent({
    event_type: eventType,
    section,
    ...additionalData,
    error: {
      error_type: errorType,
      error_message: errorMessage,
      error_severity: 'error',
    },
  });
}

/**
 * Helper for tracking profile updates
 */
export async function trackProfileUpdate(
  userId: number,
  fieldsChanged: string[],
  additionalData?: Partial<AccountEventData>
) {
  await trackAccountEvent({
    event_type: 'profile_updated',
    section: 'profile',
    user_id: userId,
    profile_fields_changed: fieldsChanged,
    ...additionalData,
  });
}

/**
 * Helper for tracking address management
 */
export async function trackAddressAction(
  eventType: 'address_added' | 'address_updated' | 'address_deleted',
  addressId: number,
  isDefault: boolean = false,
  addressType: 'shipping' | 'billing' | 'both' = 'shipping',
  additionalData?: Partial<AccountEventData>
) {
  await trackAccountEvent({
    event_type: eventType,
    section: 'addresses',
    address_id: addressId,
    is_default_address: isDefault,
    address_type: addressType,
    ...additionalData,
  });
}

/**
 * Helper for tracking payment method management
 */
export async function trackPaymentMethodAction(
  eventType: 'payment_method_added' | 'payment_method_removed',
  paymentMethodId: string,
  paymentMethodType: 'card' | 'bank_account' | 'wallet',
  isDefault: boolean = false,
  additionalData?: Partial<AccountEventData>
) {
  await trackAccountEvent({
    event_type: eventType,
    section: 'payment_methods',
    payment_method_id: paymentMethodId,
    payment_method_type: paymentMethodType,
    is_default_payment: isDefault,
    ...additionalData,
  });
}

/**
 * Helper for tracking wallet operations
 */
export async function trackWalletOperation(
  eventType: 'wallet_deposit' | 'wallet_withdrawal',
  amount: number,
  walletTransactionId: string,
  currentBalance: number,
  additionalData?: Partial<AccountEventData>
) {
  await trackAccountEvent({
    event_type: eventType,
    section: 'wallet',
    transaction_amount: amount,
    wallet_transaction_id: walletTransactionId,
    wallet_balance: currentBalance,
    ...additionalData,
  });
}
